import { useState, useRef, useCallback, useEffect } from "react";
import { compareFace, createUserProfile, completeRegistration } from "../utils/api";

const getUploadUrl = async (sourcePath) => {
  const siteConf = (window.site && window.site.config) || {};
  const assetSourceBucket = siteConf.assetSourceBucket || "";
  const base = (window.site && window.site.submissionApiBaseUrl) || "";
  const appname = window.app && window.app.appName;
  const env = window.app && window.app.appEnvName;
  const token = (window.app && window.app.storage && window.app.storage.getItem("token")) || localStorage.getItem("token") || "";
  const resp = await fetch(
    `${base}/uploadURL?appname=${appname}&env=${env}&token=${token}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source_bucket: assetSourceBucket, sourcepath: sourcePath }),
    }
  );
  const data = await resp.json();
  const uploadUrl = data && data.result;
  if (!uploadUrl) throw new Error("Signed URL missing");
  return uploadUrl;
};

// ─── Simple webcam capture component ───────────────────────────────────────
function WebcamCapture({ onCapture, onError }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const streamRef = useRef(null);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "user" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      setStreaming(true);
    } catch (e) {
      onError("Camera access denied. Please allow camera access and try again.");
    }
  };

  useEffect(() => {
    if (streaming && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [streaming]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setStreaming(false);
  };

  useEffect(() => () => stopCamera(), []);

  const capture = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      stopCamera();
      onCapture(blob, canvas.toDataURL("image/jpeg", 0.92));
    }, "image/jpeg", 0.92);
  }, [onCapture]);

  return (
    <div className="webcam-wrapper">
      {!streaming ? (
        <button className="btn-primary webcam-start" onClick={startCamera}>
          <span className="material-symbols-outlined">photo_camera</span> Open Camera
        </button>
      ) : (
        <>
          <div className="webcam-frame">
            <video ref={videoRef} playsInline muted autoPlay className="webcam-video" />
            <div className="face-oval" />
            <p className="webcam-hint">Center your face in the oval</p>
          </div>
          <div className="webcam-actions">
            <button className="btn-ghost" onClick={stopCamera}>Cancel</button>
            <button className="btn-primary" onClick={capture}>📸 Take selfie →</button>
          </div>
        </>
      )}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}

// ─── Main Step Component ───────────────────────────────────────────────────
export default function StepFaceVerify({ session, updateSession, onNext, onBack }) {
  const [phase, setPhase] = useState("intro"); // intro | camera | processing | result
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [processStep, setProcessStep] = useState("uploading"); // uploading | comparing | profile
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCapture = async (blob, dataUrl) => {
    setSelfiePreview(dataUrl);
    setPhase("processing");
    setProcessStep("uploading");
    setError("");
    setLoading(true);

    try {
      const appname = window.app && window.app.appName;
      const sourcePath = `${appname}/faceverify/${session.userId || "guest"}/selfie${Date.now()}.jpg`;

      // 1. Get signed URL and upload selfie to S3 via PUT
      const signedUrl = await getUploadUrl(sourcePath);
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.addEventListener("load", () =>
          xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("Selfie upload failed"))
        );
        xhr.addEventListener("error", () => reject(new Error("Selfie upload failed")));
        xhr.open("PUT", signedUrl);
        xhr.setRequestHeader("Content-Type", "image/jpeg");
        xhr.send(blob);
      });
      const selfieS3Key = sourcePath;

      // 2. Compare selfie S3 key against ID document S3 key
      setProcessStep("comparing");
      const compareResult = await compareFace(selfieS3Key, session.idS3Key, session.userId);

      let verifyResult = compareResult?.result || {};

      console.log("Face verify result:", verifyResult);

      if (!verifyResult.passed) {
        setError(`${verifyResult?.error} (similarity: ${verifyResult.similarity || 0}%). Please try again with better lighting.`);
        setPhase("camera");
        return;
      }

      // 3. Create account + profile
      setProcessStep("profile");
      const profilePayload = {
        email: session.email,
        ...session.profile,
        idType: session.docType,
        idDocumentS3Key: session.idS3Key,
        selfieS3Key,
        similarityScore: verifyResult.similarity,
        livenessScore: verifyResult.livenessConfidence || verifyResult.similarity, // fallback to similarity if liveness not available
        role: session.role,
      };

      if (session.idToken) {
        await createUserProfile({ userId: session.userId, ...profilePayload });
      } else {
        const reg = await completeRegistration({ password: session.password, ...profilePayload });
        updateSession({ idToken: reg.idToken, accessToken: reg.accessToken, userId: reg.userId });
      }

      setResult(verifyResult);
      updateSession({ faceResult: verifyResult });
      setPhase("result");
    } catch (e) {
      setError(e.message);
      setPhase("camera");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="step-card">
      <div className="step-icon"><span className="material-symbols-outlined">familiar_face_and_zone</span></div>
      <h1>Face verification</h1>
      <p className="step-sub">
        We'll take a quick selfie and compare it to the photo on your {session.docType === "PASSPORT" ? "passport" : "ID"}.
      </p>

      {error && <div className="error-banner">{error}</div>}

      {phase === "intro" && (
        <div className="face-intro">
          <div className="face-tips">
            <div className="tip">
              <span className="tip-icon"><span class="material-symbols-outlined">
lightbulb
</span></span>
              <span>Find a well-lit space — avoid backlighting</span>
            </div>
            <div className="tip">
              <span className="tip-icon"><span class="material-symbols-outlined">
eyeglasses_2
</span></span>
              <span>Remove glasses if possible</span>
            </div>
            <div className="tip">
              <span className="tip-icon"><span class="material-symbols-outlined">
video_camera_front
</span></span>
              <span>Look directly at the camera</span>
            </div>
            <div className="tip">
              <span className="tip-icon"><span class="material-symbols-outlined">
theater_comedy
</span></span>
              <span>No masks, hats, or heavy filters</span>
            </div>
          </div>
          <div className="action-row">
            <button className="btn-ghost" onClick={onBack}>← Back</button>
            <button className="btn-primary" onClick={() => setPhase("camera")}>
              I'm ready →
            </button>
          </div>
        </div>
      )}

      {phase === "camera" && (
        <WebcamCapture onCapture={handleCapture} onError={setError} />
      )}

      {phase === "processing" && (
        <div className="processing-state">
          {selfiePreview && (
            <img src={selfiePreview} alt="Your selfie" className="selfie-preview" />
          )}
          <div className="processing-steps">
            <div className={`proc-step ${processStep === "uploading" ? "active" : ""}`}>
              {processStep === "uploading" ? <span className="spinner" /> : <span className="spinner-placeholder" />}
              Uploading selfie…
            </div>
            <div className={`proc-step ${processStep === "comparing" ? "active" : ""}`}>
              {processStep === "comparing" ? <span className="spinner" /> : <span className="spinner-placeholder" />}
              Comparing to ID photo…
            </div>
            <div className={`proc-step ${processStep === "profile" ? "active" : ""}`}>
              {processStep === "profile" ? <span className="spinner" /> : <span className="spinner-placeholder" />}
              Creating profile…
            </div>
          </div>
        </div>
      )}

      {phase === "result" && result && (
        <div className="result-state">
          <div className="result-badge pass">
            <span className="result-check">✓</span>
            <span>Face verified</span>
          </div>
          <div className="score-grid">
            <div className="score-card">
              <span className="score-label">Face match</span>
              <span className="score-value">{result.similarity}%</span>
            </div>
            <div className="score-card">
              <span className="score-label">Liveness</span>
              <span className="score-value">{result.livenessConfidence || result.similarity}%</span>
            </div>
          </div>
          <button className="btn-primary" onClick={onNext} style={{ marginTop: "1.5rem" }}>
            Complete setup →
          </button>
        </div>
      )}
    </div>
  );
}
